import React, { useEffect, useState } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Palette, Upload, CheckCircle, MessageSquare, 
  LogOut, Image, FileImage, Clock, AlertCircle, Send, Eye
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import api from '../utils/api';
import { toast } from 'sonner';

const CreativeDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, approvalsRes] = await Promise.all([
        api.getProjects(),
        api.getPendingApprovals().catch(() => ({ data: [] }))
      ]);
      setProjects(projectsRes.data);
      // Filter design-related approvals
      setApprovals(approvalsRes.data.filter(a => 
        a.approval_type === 'design' || a.approval_type === 'deliverable'
      ));
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const handleSubmitDesign = async (projectId, title, description, files) => {
    try {
      await api.submitApproval({
        project_id: projectId,
        approval_type: 'design',
        title,
        description,
        data: { files },
        attachments: files
      });
      toast.success('Design submitted for approval');
      fetchData();
    } catch (error) {
      toast.error('Failed to submit design');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-500/20 text-green-400';
      case 'pending': return 'bg-yellow-500/20 text-yellow-400';
      case 'rejected': return 'bg-red-500/20 text-red-400';
      case 'revision_requested': return 'bg-orange-500/20 text-orange-400';
      default: return 'bg-white/10 text-white/60';
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="creative-dashboard">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-[#0A0A0F] border-r border-white/5 p-6 z-50">
        <Link to="/" className="flex items-center space-x-2 mb-10">
          <div className="text-2xl font-bold tracking-tight">
            <span className="text-[#D4AF37]">NEHPETS</span>
            <span className="text-white">+</span>
          </div>
        </Link>

        <div className="space-y-2 mb-10">
          <Link
            to="/dashboard/creative"
            className="flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-[#D4AF37] hover:bg-white/5 transition-all rounded"
          >
            <LayoutDashboard size={20} />
            <span>Overview</span>
          </Link>
          <Link
            to="/dashboard/creative/designs"
            className="flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-[#D4AF37] hover:bg-white/5 transition-all rounded"
          >
            <Palette size={20} />
            <span>My Designs</span>
          </Link>
          <Link
            to="/dashboard/creative/upload"
            className="flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-[#D4AF37] hover:bg-white/5 transition-all rounded"
          >
            <Upload size={20} />
            <span>Upload Assets</span>
          </Link>
          <Link
            to="/dashboard/creative/feedback"
            className="flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-[#D4AF37] hover:bg-white/5 transition-all rounded"
          >
            <MessageSquare size={20} />
            <span>Feedback</span>
          </Link>
        </div>

        <div className="absolute bottom-6 left-6 right-6">
          <Button
            onClick={handleLogout}
            className="w-full bg-transparent border border-white/20 text-white hover:bg-white hover:text-black text-xs py-3"
          >
            <LogOut size={16} className="mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        {/* Welcome Banner */}
        <div className="bg-gradient-to-r from-[#D4AF37]/10 to-transparent border border-[#D4AF37]/20 rounded-lg p-4 mb-8 flex items-center justify-between">
          <div className="flex items-center">
            <Palette className="text-[#D4AF37] mr-3" size={24} />
            <span className="text-white/80">Welcome back, {user?.full_name || 'Creative'}!</span>
          </div>
          <span className="text-white/40 text-sm">Creative Team</span>
        </div>

        <Routes>
          <Route
            path="/"
            element={
              <div>
                <div className="mb-8">
                  <h1 className="text-4xl font-medium mb-2">Creative Dashboard</h1>
                  <p className="text-white/60">Manage designs, upload assets, and track approvals</p>
                </div>

                {loading ? (
                  <div className="text-white/60">Loading...</div>
                ) : (
                  <>
                    {/* Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                      <div className="bg-[#0A0A0F] border border-white/5 p-6 rounded">
                        <div className="flex items-center mb-4">
                          <Palette className="text-[#D4AF37]" size={24} />
                        </div>
                        <div className="text-3xl font-bold text-[#D4AF37] mb-2">
                          {projects.length}
                        </div>
                        <div className="text-white/60 text-sm uppercase tracking-wider">Active Projects</div>
                      </div>
                      <div className="bg-[#0A0A0F] border border-white/5 p-6 rounded">
                        <div className="flex items-center mb-4">
                          <Clock className="text-yellow-400" size={24} />
                        </div>
                        <div className="text-3xl font-bold text-yellow-400 mb-2">
                          {approvals.filter(a => a.status === 'pending').length}
                        </div>
                        <div className="text-white/60 text-sm uppercase tracking-wider">Pending Approvals</div>
                      </div>
                      <div className="bg-[#0A0A0F] border border-white/5 p-6 rounded">
                        <div className="flex items-center mb-4">
                          <CheckCircle className="text-green-400" size={24} />
                        </div>
                        <div className="text-3xl font-bold text-green-400 mb-2">
                          {approvals.filter(a => a.status === 'approved').length}
                        </div>
                        <div className="text-white/60 text-sm uppercase tracking-wider">Approved</div>
                      </div>
                      <div className="bg-[#0A0A0F] border border-white/5 p-6 rounded">
                        <div className="flex items-center mb-4">
                          <AlertCircle className="text-orange-400" size={24} />
                        </div>
                        <div className="text-3xl font-bold text-orange-400 mb-2">
                          {approvals.filter(a => a.status === 'revision_requested').length}
                        </div>
                        <div className="text-white/60 text-sm uppercase tracking-wider">Needs Revision</div>
                      </div>
                    </div>

                    {/* Recent Design Submissions */}
                    <div className="mb-8">
                      <h2 className="text-xl font-medium mb-4">Recent Design Submissions</h2>
                      {approvals.length === 0 ? (
                        <div className="bg-[#0A0A0F] border border-white/5 p-6 text-center text-white/60 rounded">
                          <Image className="mx-auto mb-3 opacity-40" size={48} />
                          <p>No design submissions yet</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {approvals.slice(0, 5).map((approval) => (
                            <div
                              key={approval.approval_id}
                              className="bg-[#0A0A0F] border border-white/5 p-4 rounded hover:border-[#D4AF37]/30 transition-all"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-4">
                                  <div className="w-12 h-12 bg-white/5 rounded flex items-center justify-center">
                                    <FileImage className="text-[#D4AF37]" size={24} />
                                  </div>
                                  <div>
                                    <h4 className="font-medium">{approval.title}</h4>
                                    <p className="text-sm text-white/60">{approval.description?.slice(0, 50)}...</p>
                                    <p className="text-xs text-white/40 mt-1">
                                      Submitted: {new Date(approval.submitted_at).toLocaleDateString()}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center space-x-4">
                                  <span className={`px-3 py-1 rounded-full text-xs ${getStatusColor(approval.status)}`}>
                                    {approval.status?.replace('_', ' ').toUpperCase()}
                                  </span>
                                  <Button size="sm" variant="ghost">
                                    <Eye size={16} />
                                  </Button>
                                </div>
                              </div>
                              {approval.status === 'revision_requested' && approval.revision_notes && (
                                <div className="mt-3 p-3 bg-orange-500/10 border border-orange-500/20 rounded text-sm">
                                  <p className="text-orange-400 font-medium mb-1">Revision Requested:</p>
                                  <p className="text-white/70">{approval.revision_notes}</p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Active Projects */}
                    <div>
                      <h2 className="text-xl font-medium mb-4">Assigned Projects</h2>
                      {projects.length === 0 ? (
                        <div className="bg-[#0A0A0F] border border-white/5 p-6 text-center text-white/60 rounded">
                          No projects assigned
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {projects.slice(0, 4).map((project) => (
                            <div
                              key={project.project_id}
                              className="bg-[#0A0A0F] border border-white/5 p-4 rounded hover:border-[#D4AF37]/30 transition-all"
                            >
                              <h4 className="font-medium mb-2">{project.project_name}</h4>
                              <p className="text-sm text-white/60 mb-3">{project.event_type}</p>
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-white/40">Phase: {project.phase?.replace('_', ' ')}</span>
                                <span className="text-[#D4AF37]">{project.event_date}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            }
          />
          
          <Route
            path="/upload"
            element={
              <UploadAssets
                projects={projects}
                onSubmit={handleSubmitDesign}
              />
            }
          />
          
          <Route path="/designs" element={<div className="text-white">My Designs - Coming Soon</div>} />
          <Route path="/feedback" element={<div className="text-white">Feedback Threads - Coming Soon</div>} />
        </Routes>
      </main>
    </div>
  );
};

// Upload Assets Component
const UploadAssets = ({ projects, onSubmit }) => {
  const [form, setForm] = useState({
    projectId: '',
    title: '',
    description: '',
    files: []
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.projectId || !form.title) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    try {
      await onSubmit(form.projectId, form.title, form.description, form.files);
      setForm({ projectId: '', title: '', description: '', files: [] });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <h1 className="text-3xl font-medium mb-6">Upload Design Assets</h1>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="text-white/80 text-sm block mb-2">Select Project *</label>
          <select
            value={form.projectId}
            onChange={(e) => setForm({ ...form, projectId: e.target.value })}
            className="w-full bg-[#0A0A0F] border border-white/10 rounded px-4 py-3 text-white"
            required
          >
            <option value="">Select a project...</option>
            {projects.map((p) => (
              <option key={p.project_id} value={p.project_id}>
                {p.project_name} - {p.event_type}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-white/80 text-sm block mb-2">Design Title *</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="e.g., Event Logo v2, Invitation Design Final"
            className="w-full bg-[#0A0A0F] border border-white/10 rounded px-4 py-3 text-white"
            required
          />
        </div>

        <div>
          <label className="text-white/80 text-sm block mb-2">Description</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Describe the design, changes made, or notes for review..."
            rows={4}
            className="w-full bg-[#0A0A0F] border border-white/10 rounded px-4 py-3 text-white"
          />
        </div>

        <div>
          <label className="text-white/80 text-sm block mb-2">Upload Files</label>
          <div className="border-2 border-dashed border-white/10 rounded-lg p-8 text-center hover:border-[#D4AF37]/50 transition-all cursor-pointer">
            <Upload className="mx-auto mb-4 text-white/40" size={48} />
            <p className="text-white/60 mb-2">Drag and drop files here, or click to browse</p>
            <p className="text-white/40 text-sm">Supports: PNG, JPG, PDF, AI, PSD (max 50MB)</p>
            <input
              type="file"
              multiple
              className="hidden"
              accept=".png,.jpg,.jpeg,.pdf,.ai,.psd"
            />
          </div>
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="w-full bg-[#D4AF37] hover:bg-[#B8963A] text-black py-4"
        >
          {loading ? 'Submitting...' : 'Submit for Approval'}
          <Send size={16} className="ml-2" />
        </Button>
      </form>
    </div>
  );
};

export default CreativeDashboard;
